package fyi.moko.hotpl.ui.account

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import fyi.moko.hotpl.domain.usecase.LinkAccountUseCase
import fyi.moko.hotpl.ui.account.webauth.GoogleAuthProvider
import fyi.moko.hotpl.ui.account.webauth.deleteApp
import fyi.moko.hotpl.ui.account.webauth.getAuth
import fyi.moko.hotpl.ui.account.webauth.initializeApp
import fyi.moko.hotpl.ui.account.webauth.signInWithPopup
import kotlinx.coroutines.await
import kotlinx.coroutines.launch

/**
 * JS/web actual: Google sign-in via the Firebase JS popup flow.
 *
 * GitLive doesn't expose `linkWithPopup`, and signing in on the primary app would
 * abandon the anonymous session before [LinkAccountUseCase] can carry its local data
 * over. So we harvest a Google credential (id + access token) via `signInWithPopup` on
 * an ephemeral **secondary** Firebase app — leaving the primary anonymous user intact —
 * and hand it back as a [LinkAccountUseCase.SocialCredential]. The shared pipeline then
 * links it onto the anonymous user (or, on a credential conflict, switches accounts and
 * re-brands the local places), exactly like Android/iOS.
 *
 * The popup is opened synchronously inside the click's user-gesture so the browser
 * doesn't block it; only the result is awaited on a coroutine.
 */
@Composable
actual fun rememberSocialSignInLauncher(): SocialSignInLauncher {
    val scope = rememberCoroutineScope()
    return remember {
        SocialSignInLauncher { provider, onResult ->
            if (provider != LinkAccountUseCase.Provider.GOOGLE) {
                onResult(Result.failure(SocialSignInUnavailableException("Only Google sign-in is available on web right now")))
                return@SocialSignInLauncher
            }

            val secondaryApp = initializeApp(googlePopupConfig(), "hotpl-google-popup-" + js("Date.now()"))
            val popup = try {
                // Called in the gesture so the popup isn't blocked.
                signInWithPopup(getAuth(secondaryApp), GoogleAuthProvider())
            } catch (e: Throwable) {
                scope.launch { runCatching { deleteApp(secondaryApp).await() } }
                onResult(Result.failure(SocialSignInUnavailableException(e.message ?: "Google sign-in failed")))
                return@SocialSignInLauncher
            }

            scope.launch {
                val result = try {
                    val userCredential = popup.await()
                    val credential = GoogleAuthProvider.credentialFromResult(userCredential)
                    val idToken = credential?.idToken as? String
                    if (idToken == null) {
                        Result.failure(SocialSignInUnavailableException("Google sign-in returned no ID token"))
                    } else {
                        Result.success(
                            LinkAccountUseCase.SocialCredential(idToken, credential.accessToken as? String),
                        )
                    }
                } catch (e: Throwable) {
                    when (e.asDynamic().code as? String) {
                        "auth/popup-closed-by-user", "auth/cancelled-popup-request" ->
                            Result.failure(SocialSignInCancelledException())
                        else ->
                            Result.failure(SocialSignInUnavailableException(e.message ?: "Google sign-in failed"))
                    }
                } finally {
                    runCatching { deleteApp(secondaryApp).await() }
                }
                onResult(result)
            }
        }
    }
}

/**
 * Config for the ephemeral popup app — `Hotpla Web`의 공개 웹 값이다.
 *
 * `Main.kt`의 기본 앱과 같은 값을 쓴다. 예전에는 두 곳이 Android 앱 값을 빌려 썼고 여기만
 * authDomain을 갖고 있었는데, 이제 웹 앱이 등록돼 양쪽이 같은 웹 config를 쓴다.
 * 이 값들이 왜 비밀이 아닌지는 `Main.kt`의 `initFirebase` 주석 참고.
 */
private fun googlePopupConfig(): dynamic {
    val config: dynamic = js("{}")
    config.apiKey = "AIzaSyAtRN5oFKHohDPtrpErx5ZSN4okon8vyRw"
    config.authDomain = "hotpla-app.firebaseapp.com"
    config.projectId = "hotpla-app"
    config.appId = "1:597866223762:web:7388e4fcce2dafcb70ae4b"
    config.messagingSenderId = "597866223762"
    return config
}
